import {
  DocumentIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { formatTime } from '../../utils/date.util'
import DocumentStatus from '../documents/DocumentStatus'
import { SHOP_DOCUMENT_STATUSES } from '../../constants/shopDocumentStatuses'
import PDFViewer from './PDFViewer'
import ApproveDocumentModal from '../admin/ApproveDocumentModal'
import RejectDocumentModal from '../admin/RejectDocumentModal'
import { useState } from 'react'
import { useDocumentReview } from '../../hooks/useDocumentReview'

export default function DocumentListing({ documents = [], shopId, userType, readOnly = false }) {
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false)
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [documentToReview, setDocumentToReview] = useState(null)

  const { approveDocument, rejectDocument, isLoading } = useDocumentReview(shopId, userType)

  const handleViewDocument = (document) => {
    if (document.filePath) {
      setSelectedDocument(document)
      setIsPDFViewerOpen(true)
    }
  }

  const handleClosePDFViewer = () => {
    setIsPDFViewerOpen(false)
    setSelectedDocument(null)
  }

  const handleApproveClick = (document) => {
    setDocumentToReview(document)
    setIsApproveModalOpen(true)
  }

  const handleRejectClick = (document) => {
    setDocumentToReview(document)
    setIsRejectModalOpen(true)
  }

  const handleApproveConfirm = async () => {
    if (documentToReview) {
      try {
        await approveDocument(documentToReview.id)
        setIsApproveModalOpen(false)
        setDocumentToReview(null)
      } catch (error) {
        // Error is handled by the hook
      }
    }
  }

  const handleRejectConfirm = async (rejectReason) => {
    if (documentToReview) {
      try {
        await rejectDocument(documentToReview.id, rejectReason)
        setIsRejectModalOpen(false)
        setDocumentToReview(null)
      } catch (error) {
        // Error is handled by the hook
      }
    }
  }

  const handleCloseApproveModal = () => {
    if (!isLoading) {
      setIsApproveModalOpen(false)
      setDocumentToReview(null)
    }
  }

  const handleCloseRejectModal = () => {
    if (!isLoading) {
      setIsRejectModalOpen(false)
      setDocumentToReview(null)
    }
  }

  const renderActions = (document) => {
    if (readOnly) return null

    const actions = []

    if (document?.status !== SHOP_DOCUMENT_STATUSES.approved.key) {
      actions.push(
        <button
          key="approve"
          onClick={() => handleApproveClick(document)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
          disabled={isLoading}
        >
          Approve
        </button>
      )
    }

    if (document?.status !== SHOP_DOCUMENT_STATUSES.rejected.key) {
      actions.push(
        <button
          key="reject"
          onClick={() => handleRejectClick(document)}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
          disabled={isLoading}
        >
          Reject
        </button>
      )
    }

    return actions
  }

  return (
    <div className="w-full mt-8">
      <h2 className="text-[24px] font-bold text-[#545454] mb-6">Documents ({documents.length})</h2>

      {documents.length === 0 ? (
        <div className="bg-background rounded-lg border border-gray-200 p-8 text-center">
          <DocumentIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">No documents submitted</p>
          <p className="text-gray-400 text-sm mt-1">Documents will appear here when submitted</p>
        </div>
      ) : (
        <div className="space-y-4">
          {documents.map((document) => (
            <div
              key={document.id}
              className="bg-background rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <DocumentIcon className="w-6 h-6 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {document.documentType?.name || 'Unknown Document'}
                    </h3>
                    <DocumentStatus status={document.status} />
                  </div>

                  {document.documentType?.description && (
                    <p className="text-gray-600 text-sm mb-4">
                      {document.documentType.description}
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <CalendarIcon className="w-4 h-4" />
                      <span>Submitted: {formatTime(document.createdAt)}</span>
                    </div>

                    {document.approvedAt && (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircleIcon className="w-4 h-4" />
                        <span>Approved: {formatTime(document.approvedAt)}</span>
                      </div>
                    )}

                    {document.rejectedAt && (
                      <div className="flex items-center gap-2 text-red-600">
                        <XCircleIcon className="w-4 h-4" />
                        <span>Rejected: {formatTime(document.rejectedAt)}</span>
                      </div>
                    )}

                    {document.approver && (
                      <div className="text-gray-600">
                        <span>Approved by: {document.approver?.name}</span>
                      </div>
                    )}

                    {document.rejector && (
                      <div className="text-gray-600">
                        <span>Rejected by: {document.rejector?.name}</span>
                      </div>
                    )}
                  </div>

                  {document.rejectReason && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-700 text-sm">
                        <strong>Rejection Reason:</strong> {document.rejectReason}
                      </p>
                    </div>
                  )}
                </div>

                <div className="ml-4 flex flex-col gap-2">
                  <button
                    onClick={() => handleViewDocument(document)}
                    className="bg-secondary-600 hover:bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                    disabled={!document.filePath}
                  >
                    View Document
                  </button>

                  {renderActions(document)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PDFViewer
        isOpen={isPDFViewerOpen}
        onClose={handleClosePDFViewer}
        pdfUrl={selectedDocument?.filePath}
        documentName={selectedDocument?.documentType?.name || 'Document'}
      />

      <ApproveDocumentModal
        isOpen={isApproveModalOpen && !readOnly}
        onClose={handleCloseApproveModal}
        onConfirm={handleApproveConfirm}
        documentName={documentToReview?.documentType?.name || 'Document'}
        isLoading={isLoading}
      />

      <RejectDocumentModal
        isOpen={isRejectModalOpen && !readOnly}
        onClose={handleCloseRejectModal}
        onConfirm={handleRejectConfirm}
        documentName={documentToReview?.documentType?.name || 'Document'}
        isLoading={isLoading}
      />
    </div>
  )
}
